import { Link, useParams } from 'react-router-dom'
import { Avatar } from '../../components/Avatar'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { Col } from '../../components/Col'
import { Container } from '../../components/Container'
import { Input } from '../../components/Input'
import { ProgressBar } from '../../components/ProgressBar'
import { Row } from '../../components/Row'
import Telegram from '../../components/svg/Telegram'
import Twitter from '../../components/svg/Twitter'
import styles from './styles.module.css'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TransactionTable } from '../../containers/TransactionTable'
import { fetchOrders, fetchToken } from '../../services/tokenService'
import { Order, TokenItem } from '../../interfaces/token'
import { useDataContext } from '../../providers/data'
import Heart from '../../components/svg/Heart'
import PlusSmall from '../../components/svg/PlusSmall'
import { useWindowSize } from '../../hooks/useWindowSize'
import { BottomTabs } from '../../components/BottomTabs'
import Zap from '../../components/svg/Zap'
import Chart from '../../components/svg/Chart'
import Trade from '../../components/svg/Trade'
import Table from '../../components/svg/Table'
import {
  buyToken,
  getTokens,
  LABELS,
  sellToken
} from '../../services/web3Service'
import { useWallet } from '@solana/wallet-adapter-react'
import { PublicKey } from '@solana/web3.js'
import toast from 'react-hot-toast'
import { useModalContext } from '../../providers/modal'
import { SubmitHandler, useForm } from 'react-hook-form'
import Close from '../../components/svg/Close'
import { Loader } from '../../components/Loader'
import Success from '../../components/svg/Success'
import { ChartComponent } from './components/Chart'
import { BaseWalletMultiButton } from '@solana/wallet-adapter-react-ui'

const ALLOWED_KEYS = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Tab'
]

enum TradeType {
  Buy,
  Sell
}

export const Detail = () => {
  const { setContent, setIsModalOpen } = useModalContext()
  const [token, setToken] = useState<TokenItem | null>(null)
  const [orders, setOrders] = useState<Order[]>([])
  const [error, setError] = useState<{
    error: string
    message: string
    statusCode: number
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const { mint } = useParams<{ mint: string }>()
  const { isLoading, orderSubscribe, orderUnsubscribe } = useDataContext()
  const wallet = useWallet()
  const { width } = useWindowSize()
  const [selectedTab, setSelectedTab] = useState(0)
  const [tradeType, setTradeType] = useState<TradeType>(TradeType.Buy)
  const [tradeAmount, setTradeAmount] = useState<string>('')
  const [tokenAmount, setTokenAmount] = useState<string>('0')
  const [trading, setTrading] = useState(false)
  const [solRealToSpendAmount, setSolRealToSpendAmount] = useState<
    number | null
  >(null)
  const toastTradeInfoSolRef = useRef<HTMLDivElement | null>(null)
  const parsePercent = useCallback(
    (percent: number) => {
      if (percent === 1 && Number(tokenAmount) !== 0) {
        return tokenAmount.replace(/,/g, '.')
      }

      return Number(tokenAmount) === 0
        ? '0.0'
        : parseFloat(`${Number(tokenAmount) * percent}`)
            .toPrecision(12)
            .replace(/,/g, '.')
    },
    [tokenAmount]
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm()

  useEffect(() => {
    console.log({ mint, isLoading })

    const fetchData = async () => {
      try {
        const data = await fetchToken(mint || '')
        const orderData = await fetchOrders(mint || '')
        console.log({ data })
        setToken(data)
        setOrders(orderData)
        if (localStorage.getItem('createdToken')) {
          localStorage.removeItem('createdToken')
        }
        setLoading(false)
        setError(null)
        return data
      } catch (error: any) {
        console.log('Token is not created yet')
        console.log(error)
        const createdToken = localStorage.getItem('createdToken')
        if (createdToken === mint) {
          console.log('retrying', createdToken)
          setTimeout(() => {
            fetchData()
          }, 1000)
        } else {
          setLoading(false)
          if (error.response) {
            setError({
              error: error.response.data?.error || 'Error',
              message: error.response.data?.message || 'Something went wrong',
              statusCode: error.response.data?.statusCode || ''
            })
          } else {
            toast.error(error.message)
          }
        }
      }
    }
    if (!mint || isLoading) {
      return
    }
    fetchData()

    orderSubscribe(mint, (data) => {
      console.log('orderSubscribe', data)
      const order: Order = data.order
      if (!order) {
        console.log('no order in data', data)
        return
      }
      if (!order.transaction_signature) {
        order.transaction_signature =
          order.instruction_key?.transaction_signature || ''
      }
      setOrders((prev) => {
        const output = prev.filter(
          (item) => item.transaction_signature !== order.transaction_signature
        )
        return [order, ...output].sort((a, b) =>
          new Date(a.time).getTime() < new Date(b.time).getTime() ? 1 : -1
        )
      })
      const tokenStats = data.tokenStats
      console.log('tokenStatsSubscribeData', { tokenStats })
      if (!tokenStats) {
        console.log('no tokenStats in data', data)
        return
      }
      setToken((prev) => {
        if (!prev) {
          return prev
        }
        return {
          ...prev,
          stats: {
            ...prev.stats,
            ...tokenStats
          }
        }
      })
    })

    return () => {
      orderUnsubscribe(mint)
    }
  }, [mint, isLoading])

  const fetchTokenData = useCallback(async () => {
    if (!mint || !wallet?.publicKey) {
      return
    }
    const data = await getTokens(wallet, mint)
    if (data?.tokenBalance?.value?.uiAmountString) {
      setTokenAmount(data.tokenBalance.value.uiAmountString)
    }
  }, [wallet, mint])

  useEffect(() => {
    fetchTokenData()
  }, [wallet, mint])

  useEffect(() => {
    setTradeAmount('')
  }, [tradeType])

  const tradeToastContent = useCallback(
    (type: 'loading' | 'success') => {
      if (!token) {
        return null
      }
      const tokenPrice = token.stats.price / 1000
      return (
        <div
          className={styles.toastContainer}
          style={
            type === 'success' ? { borderColor: 'var(--accent-green)' } : {}
          }
        >
          <div className={styles.toastInfoBlock}>
            <div className={styles.toastTradeInfo}>
              {tradeType === TradeType.Buy ? 'Buy ' : 'Sell '}
              {tradeType === TradeType.Buy
                ? parseFloat(`${Number(tradeAmount) / tokenPrice}`).toPrecision(
                    12
                  )
                : parseFloat(`${Number(tradeAmount)}`)}{' '}
              {token?.token.meta.symbol || ''} for
            </div>
            <div
              className={styles.toastTradeInfo}
              style={type === 'success' ? { color: 'var(--accent-green)' } : {}}
              ref={toastTradeInfoSolRef}
            >
              {tradeType === TradeType.Buy
                ? solRealToSpendAmount || parseFloat(`${Number(tradeAmount)}`)
                : solRealToSpendAmount ||
                  parseFloat(`${Number(tradeAmount) * tokenPrice}`).toPrecision(
                    12
                  )}{' '}
              SOL
            </div>
            {type === 'loading' && (
              <div className={styles.toastTradeStatus}>
                <Loader /> <span>Confirming transaction</span>
              </div>
            )}
            {type === 'success' && (
              <div
                className={styles.toastTradeStatus}
                style={{ color: 'var(--accent-green)' }}
              >
                <Success /> <span>Transaction confirmed</span>
              </div>
            )}
          </div>
          <Link
            to={`https://solscan.io/token/${mint}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.toastLink}
          >
            <Button className={styles.toastViewTx}>View tx</Button>
          </Link>
        </div>
      )
    },
    [tradeType, tradeAmount, token, solRealToSpendAmount, mint]
  )

  const onSubmit: SubmitHandler<any> = useCallback(() => {
    try {
      toast.success('Comment posted')
      setIsModalOpen(false)
      setContent(null)
      reset()
    } catch (error: any) {
      toast.error(error.message)
    }
  }, [setContent, setIsModalOpen, reset])

  const comment = useCallback(() => {
    setContent(
      <Card className={styles.commentModal}>
        <div className={styles.header}>
          <div className={styles.title}>Add comment</div>
          <div
            className={styles.close}
            onClick={() => {
              setIsModalOpen(false)
              setContent(null)
            }}
          >
            <Close />
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.commentForm}>
          <label className={styles.inputContainer}>
            <textarea
              className={styles.textarea}
              placeholder="Write your comment here"
              {...register('commentText', { required: true })}
            />
            {errors.commentText && (
              <span className={styles.error}>This field is required</span>
            )}
          </label>
          <Button type="submit" className={styles.submitButton}>
            Post comment
          </Button>
        </form>
      </Card>
    )
    setIsModalOpen(true)
  }, [setContent, setIsModalOpen, errors, handleSubmit, onSubmit, register])

  const trade = useCallback(async () => {
    let multiplier = 1
    let tradeToast = ''
    try {
      if (!mint) {
        toast.error('no mint')
        return
      }
      if (!wallet) {
        toast.error('no wallet')
        return
      }
      if (!tradeAmount || Number(tradeAmount) <= 0) {
        toast.error('no trade amount')
        return
      }
      if (!token) {
        toast.error('missing token info')
        return
      }
      if (tradeAmount.indexOf('.') !== -1) {
        multiplier = Math.pow(10, tradeAmount.split('.')[1].length)
      }
      const amount =
        ((tradeType === TradeType.Sell ? 1_000_000n : 1_000_000_000n) *
          BigInt(Math.round(Number(tradeAmount) * multiplier))) /
        BigInt(multiplier)
      setTrading(true)
      setSolRealToSpendAmount(null)
      tradeToast = toast.loading(tradeToastContent('loading'))
      const request = async () => {
        if (tradeType === TradeType.Sell) {
          const result = await sellToken(
            wallet,
            {
              mint: new PublicKey(mint),
              creator: new PublicKey(token.token.creator),
              tokenToSpend: amount,
              minSolToReceive: 0n // todo: what is the proper value?
            },
            (val) => {
              if (toastTradeInfoSolRef.current) {
                toastTradeInfoSolRef.current.innerText = `${
                  Math.floor(Math.abs(val / 10000)) / 100000
                } SOL`
              }
              setSolRealToSpendAmount(
                Math.floor(Math.abs(val / 10000)) / 100000
              )
            }
          )
          console.log(result)
        } else if (tradeType === TradeType.Buy) {
          const result = await buyToken(
            wallet,
            {
              mint: new PublicKey(mint),
              creator: new PublicKey(token.token.creator),
              solToSpend: amount,
              minTokenToReceive: 0n // todo: what is the proper value?
            },
            (val) => {
              if (toastTradeInfoSolRef.current) {
                toastTradeInfoSolRef.current.innerText = `${
                  Math.floor(Math.abs(val / 10000)) / 100000
                } SOL`
              }
              setSolRealToSpendAmount(
                Math.floor(Math.abs(val / 10000)) / 100000
              )
            }
          )
          console.log(result)
        }
      }
      await request()
      toast.success(tradeToastContent('success'), {
        id: tradeToast,
        duration: 10000
      })
      setTrading(false)
      fetchTokenData()
    } catch (error: any) {
      setTrading(false)
      toast.dismiss(tradeToast)
      console.log(error)
      toast.error(
        `${tradeType === TradeType.Buy ? 'Buy' : 'Sell'} transaction failed: ${
          String(error).indexOf('insufficient funds') !== -1
            ? 'Insufficient funds'
            : 'something went wrong'
        }`
      )
    }
  }, [
    tradeType,
    mint,
    wallet,
    tradeAmount,
    token,
    toastTradeInfoSolRef,
    fetchTokenData,
    tradeToastContent
  ])

  const tradeBlock = useMemo(
    () => (
      <Card className={styles.tradeCard}>
        <div className={styles.buySellWrapper}>
          <Button
            className={`${styles.buyTab} ${
              tradeType === TradeType.Buy ? '' : styles.inactiveTab
            }`}
            onClick={() => setTradeType(TradeType.Buy)}
          >
            Buy
          </Button>
          <Button
            className={`${styles.sellTab} ${
              tradeType === TradeType.Sell ? '' : styles.inactiveTab
            }`}
            onClick={() => setTradeType(TradeType.Sell)}
          >
            Sell
          </Button>
        </div>
        {tradeType === TradeType.Buy && (
          <>
            <Input
              placeholder="0.0"
              className={styles.tradeInput}
              type="text"
              postfix="SOL"
              value={tradeAmount}
              onChange={setTradeAmount}
              onKeyDown={(e) => {
                if (tradeAmount === '' && e.key === '.') {
                  setTradeAmount('0.')
                }
                if (
                  !/^[+]?([.]\d+|\d+[.]?\d*)$/.test(tradeAmount + e.key) &&
                  ALLOWED_KEYS.indexOf(e.key) === -1
                ) {
                  e.preventDefault()
                }
              }}
            />
            <div className={styles.chipsWrapper}>
              <Button
                className={styles.chipButton}
                onClick={() => setTradeAmount('1')}
              >
                1 SOL
              </Button>
              <Button
                className={styles.chipButton}
                onClick={() => setTradeAmount('2')}
              >
                2 SOL
              </Button>
              <Button
                className={styles.chipButton}
                onClick={() => setTradeAmount('5')}
              >
                5 SOL
              </Button>
              <Button
                className={styles.chipButton}
                onClick={() => setTradeAmount('10')}
              >
                10 SOL
              </Button>
            </div>
          </>
        )}
        {tradeType === TradeType.Sell && (
          <>
            <Input
              placeholder="0.0"
              className={styles.tradeInput}
              type="text"
              value={tradeAmount}
              onChange={setTradeAmount}
              onKeyDown={(e) => {
                if (tradeAmount === '' && e.key === '.') {
                  setTradeAmount('0.')
                }
                if (
                  !/^[+]?([.]\d+|\d+[.]?\d*)$/.test(tradeAmount + e.key) &&
                  ALLOWED_KEYS.indexOf(e.key) === -1
                ) {
                  e.preventDefault()
                }
              }}
              postfix={token?.token.meta.symbol || ''} // todo: check if this is correct
            />
            <div className={styles.chipsWrapper}>
              <Button
                className={styles.chipButton}
                onClick={() => setTradeAmount(parsePercent(0.25))}
              >
                25%
              </Button>
              <Button
                className={styles.chipButton}
                onClick={() => setTradeAmount(parsePercent(0.5))}
              >
                50%
              </Button>
              <Button
                className={styles.chipButton}
                onClick={() => setTradeAmount(parsePercent(0.75))}
              >
                75%
              </Button>
              <Button
                className={styles.chipButton}
                onClick={() => setTradeAmount(parsePercent(1))}
              >
                100%
              </Button>
            </div>
          </>
        )}
        {wallet?.publicKey ? (
          <Button
            className={`${styles.tradeButton} ${
              tradeType === TradeType.Buy ? styles.buyButton : styles.sellButton
            }`}
            onClick={trade}
            disabled={
              trading || !!token?.token.released_at || !token?.stats.isTradeable
            }
          >
            Trade
          </Button>
        ) : (
          <BaseWalletMultiButton
            style={{
              width: '100%',
              color: 'white',
              padding: 11,
              fontSize: 20,
              fontWeight: 600,
              lineHeight: 22.8,
              justifyContent: 'center',
              backgroundColor: '#0b9d60',
              borderRadius: 100,
              fontFamily: 'Pangram, sans-serif'
            }}
            labels={{ ...LABELS, 'no-wallet': 'Connect wallet' }}
          />
        )}
      </Card>
    ),
    [
      tradeType,
      trade,
      tradeAmount,
      token,
      setTradeAmount,
      trading,
      parsePercent,
      wallet
    ]
  )

  const infoBlock = useMemo(
    () => (
      <>
        <div className={styles.links}>
          {token?.token.meta.socialLinks.site && (
            <a
              className={styles.socialLink}
              target="_blank"
              rel="noopener noreferrer"
              href={token?.token.meta.socialLinks.site}
            >
              Site
            </a>
          )}
          {token?.token.meta.socialLinks.twitter && (
            <a
              className={styles.socialLink}
              target="_blank"
              rel="noopener noreferrer"
              href={token?.token.meta.socialLinks.twitter}
            >
              <Twitter width={24} height={24} />
              Twitter
            </a>
          )}
          {token?.token.meta.socialLinks.telegram && (
            <a
              className={styles.socialLink}
              href={token?.token.meta.socialLinks.telegram}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Telegram width={24} height={24} />
              Telegram
            </a>
          )}
        </div>
        <div className={styles.infoWrapper}>
          <div className={styles.infoImage}>
            <Avatar
              src={token?.token.meta.imageUrl || ''}
              size={133}
              noborder
            />
          </div>
          <div className={styles.info}>
            <h3 className={styles.infoName}>
              {token?.token.meta.name || ''}{' '}
              {token?.token.meta.symbol ? `(${token?.token.meta.symbol})` : ''}
            </h3>
            <p className={styles.infoDescription}>
              {token?.token.meta.description || ''}
            </p>
          </div>
        </div>
        <div className={styles.bondingWrapper}>
          <h4 className={styles.percent}>
            Bonding curve progress:{' '}
            {Math.round((token?.stats.bondingRate || 0) * 100) || 0}%
          </h4>
          <ProgressBar
            progress={Math.round((token?.stats.bondingRate || 0) * 100) || 0}
            withLabel={false}
          />
          <div className={styles.bondingInfo}>
            when the market cap reaches $0 all the liquidity from the bonding
            curve will be deposited into Raydium and burned. progression
            increases as the price goes up. there are 0 tokens still available
            for sale in the bonding curve and there is 0 SOL in the bonding
            curve.
          </div>
        </div>
      </>
    ),
    [token]
  )

  const ordersBlock = useMemo(
    () => (
      <TransactionTable data={orders} tokenName={token?.token.meta.symbol} />
    ),
    [orders, token]
  )

  const commentBlock = useMemo(
    () => (
      <Card className={styles.commentCard}>
        <Button className={styles.commentButton} onClick={comment}>
          <span>Comment</span>
          <div>
            <PlusSmall />
          </div>
        </Button>
        <div className={styles.replyWrapper}>
          <div className={styles.replyHeader}>
            <Avatar src={'/doge.png'} size={24} type={'circle'} />
            <span className={styles.replyAuthor}>Kopernick</span>
            <span>24:02</span>
            <span>Reply</span>
            <span>
              <Heart />
              1111
            </span>
          </div>
          <div className={styles.replyContent}>
            When the market cap reached zero all the liquidity from the bonding
            curve will be deposited inti Raydium and burn progression increases
            as the price goes up.When the market cap reached zero all the
            liquidity from the bonding curve will be deposited inti Raydium and
            burn progression increases as the price goes up.
          </div>
        </div>
      </Card>
    ),
    [comment]
  )

  const bottomTabs = [
    {
      label: 'info',
      icon: <Zap />
    },
    {
      label: 'chart',
      icon: <Chart />
    },
    {
      label: 'trade',
      icon: <Trade />
    },
    {
      label: 'txs',
      icon: <Table />
    }
  ]

  if (loading) {
    return (
      <div className={styles.container}>
        <Container>
          <div className={styles.loaderWrapper}>
            Loading <Loader size={40} />
          </div>
        </Container>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.container}>
        <Container>
          <div className={styles.errorContainer}>
            <h1>{error.error}</h1>
            <p>{error.message}</p>
            <p>{error.statusCode}</p>
            <Link className={styles.goToMain} to={'/'}>
              <Button>Go to main</Button>
            </Link>
          </div>
        </Container>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <Container>
        {width > 1024 ? (
          <div className={styles.desktopGrid}>
            <Row>
              <Col size={8} lg={{ size: 7 }}>
                <ChartComponent
                  loading={loading}
                  error={error}
                  orders={orders}
                  tokenPrice={token?.stats.price || 0}
                />
              </Col>
              <Col size={4} lg={{ size: 5 }}>
                {tradeBlock}
                {infoBlock}
              </Col>
            </Row>
            <Row>
              <Col size={8} lg={{ size: 7 }}>
                {ordersBlock}
              </Col>
              <Col size={4} lg={{ size: 5 }}>
                {commentBlock}
              </Col>
            </Row>
          </div>
        ) : (
          <div className={styles.mobileTabContent}>
            {selectedTab === 0 && infoBlock}
            {selectedTab === 1 && (
              <ChartComponent
                loading={loading}
                error={error}
                orders={orders}
                tokenPrice={token?.stats.price || 0}
              />
            )}
            {selectedTab === 2 && (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 23 }}
              >
                {tradeBlock}
                {commentBlock}
              </div>
            )}
            {selectedTab === 3 && ordersBlock}
          </div>
        )}
      </Container>
      {width && width <= 1024 && (
        <BottomTabs
          items={bottomTabs}
          selected={selectedTab}
          onChange={setSelectedTab}
        />
      )}
    </div>
  )
}
